export interface DeviceHintReport {
  ua: string;
  full_version_list: string;
  platform: string;
  platform_version: string;
  model: string;
  mobile: string;
  architecture: string;
  bitness: string;
  form_factors: string;
}

export interface DeviceReport {
  user_agent: string;
  client_hints: DeviceHintReport;
}

interface UABrand {
  brand: string;
  version: string;
}

interface HighEntropyValues {
  brands?: UABrand[];
  fullVersionList?: UABrand[];
  platform?: string;
  platformVersion?: string;
  model?: string;
  mobile?: boolean;
  architecture?: string;
  bitness?: string;
  formFactors?: string[];
  uaFullVersion?: string;
}

interface UserAgentData {
  brands?: UABrand[];
  mobile?: boolean;
  platform?: string;
  getHighEntropyValues?: (hints: string[]) => Promise<HighEntropyValues>;
}

const emptyHints = (): DeviceHintReport => ({
  ua: "",
  full_version_list: "",
  platform: "",
  platform_version: "",
  model: "",
  mobile: "",
  architecture: "",
  bitness: "",
  form_factors: "",
});

function brandsToHeader(brands: UABrand[] | undefined): string {
  if (!brands?.length) return "";
  return brands
    .filter((item) => item.brand && item.version)
    .map((item) => `"${item.brand}";v="${item.version}"`)
    .join(", ");
}

function readUserAgentData(): UserAgentData | undefined {
  if (typeof navigator === "undefined") return undefined;
  return (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData;
}

/**
 * Collects the browser, OS, and device model available to this page.
 * High-entropy values come from navigator.userAgentData when the browser allows it.
 * The server still composes the stored label.
 */
export async function collectDeviceProfile(): Promise<DeviceReport> {
  const userAgent = typeof navigator === "undefined" ? "" : navigator.userAgent;
  const hints = emptyHints();
  const data = readUserAgentData();
  if (!data) return { user_agent: userAgent, client_hints: hints };
  hints.ua = brandsToHeader(data.brands);
  hints.mobile = data.mobile ? "?1" : "?0";
  hints.platform = data.platform ?? "";
  if (!data.getHighEntropyValues) return { user_agent: userAgent, client_hints: hints };
  try {
    const values = await data.getHighEntropyValues([
      "architecture",
      "bitness",
      "model",
      "platform",
      "platformVersion",
      "uaFullVersion",
      "fullVersionList",
      "formFactors",
      "wow64",
    ]);
    hints.full_version_list = brandsToHeader(values.fullVersionList);
    hints.platform = values.platform || hints.platform;
    hints.platform_version = values.platformVersion ?? "";
    hints.model = values.model ?? "";
    hints.architecture = values.architecture ?? "";
    hints.bitness = values.bitness ?? "";
    hints.form_factors = values.formFactors?.join(", ") ?? "";
    if (typeof values.mobile === "boolean") hints.mobile = values.mobile ? "?1" : "?0";
    if (!hints.full_version_list && values.uaFullVersion && data.brands?.[0]) {
      hints.full_version_list = brandsToHeader(data.brands.map((item) => ({ brand: item.brand, version: values.uaFullVersion ?? item.version })));
    }
  } catch {
    // A denied hint prompt still leaves the low-entropy brands and the UA.
  }
  return { user_agent: userAgent, client_hints: hints };
}
